import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { settingsService } from '../services/settingsService';
import MegaMenu from './MegaMenu';
import BookServiceModal from './BookServiceModal';

const { FiMenu, FiX, FiPhone, FiMail, FiMapPin, FiClock, FiSettings, FiZap, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookServiceOpen, setIsBookServiceOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showGolfCartDropdown, setShowGolfCartDropdown] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [settings, setSettings] = useState(null);
  const [mobileAccordions, setMobileAccordions] = useState({
    services: false,
    dealership: false
  });
  const location = useLocation();
  
  // Hover timeout refs for dropdowns
  const servicesTimeoutRef = useRef(null);
  const golfCartTimeoutRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const { data } = await settingsService.getSettings();
    if (data) {
      setSettings(data);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if scrolled
      setIsScrolled(currentScrollY > 50);
      
      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past 100px
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      // Reset accordions when closing menu
      setMobileAccordions({
        services: false,
        dealership: false
      });
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const mainNavigation = [
    { name: 'Home', href: '/' },
    { name: 'Automobiles', href: '/vehicles' },
    { name: 'Golf Carts', href: '/golf-carts', hasDropdown: true },
    { name: 'Trailers', href: '/trailers' },
    { name: 'AI Tools', href: '/ai-tools', icon: FiZap },
    { name: 'My Payments', href: '/my-payments' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const golfCartDropdown = [
    { name: 'View Golf Carts', href: '/golf-carts' },
    { name: 'Golf Cart Services', href: '/golf-cart-services' },
  ];

  const servicesDropdown = [
    { name: 'European Vehicle Repair', href: '/services/european-car-repair' },
    { name: 'Diesel Repair', href: '/services/dallas-diesel-paradise-pros' },
    { name: '--- SPECIALIST SERVICES ---', isHeader: true },
    { name: 'BMW Service & Repair', href: '/services/european-car-repair/bmw' },
    { name: 'Mercedes Service & Repair', href: '/services/european-car-repair/mercedes' },
    { name: 'Audi Service & Repair', href: '/services/european-car-repair/audi' },
    { name: 'Porsche Service & Repair', href: '/services/european-car-repair/porsche' },
    { name: 'Volkswagen Service & Repair', href: '/services/european-car-repair/volkswagen' },
    { name: 'Jaguar Service & Repair', href: '/services/european-car-repair/jaguar' },
    { name: 'Volvo Service & Repair', href: '/services/european-car-repair/volvo' },
    { name: 'MINI Cooper Service', href: '/services/european-car-repair/mini-cooper' },
    { name: '--- REPAIR SERVICES ---', isHeader: true },
    { name: 'Clutch Repair', href: '/services/clutch-repair' },
    { name: 'Check Engine Light', href: '/services/diagnostic-check-engine-light' },
    { name: 'Wheel & Tire Service', href: '/services/wheel-and-tire' },
    { name: '--- DEALERSHIP ---', isHeader: true },
    { name: 'Financing', href: '/finance-application' },
    { name: 'Service Appointment', href: '/service' },
    { name: 'Trade-In', href: '/trade-in' },
  ];

  const mobileNavigation = [
    { name: 'Home', href: '/' },
    { name: 'Automobiles', href: '/vehicles' },
    { name: 'Golf Carts', href: '/golf-carts' },
    { name: 'Golf Cart Services', href: '/golf-cart-services' },
    { name: 'Trailers', href: '/trailers' },
    { name: '--- REPAIR SERVICES ---', isHeader: true },
    { name: 'All Repair Services', href: '/services' },
    { name: 'European Vehicles', href: '/services/european-car-repair' },
    { name: 'Diesel Repair', href: '/services/dallas-diesel-paradise-pros' },
    { name: 'BMW Service & Repair', href: '/services/european-car-repair/bmw' },
    { name: 'Mercedes Service & Repair', href: '/services/european-car-repair/mercedes' },
    { name: 'Audi Service & Repair', href: '/services/european-car-repair/audi' },
    { name: 'Porsche Service & Repair', href: '/services/european-car-repair/porsche' },
    { name: 'Volkswagen Service & Repair', href: '/services/european-car-repair/volkswagen' },
    { name: 'Jaguar Service & Repair', href: '/services/european-car-repair/jaguar' },
    { name: 'Volvo Service & Repair', href: '/services/european-car-repair/volvo' },
    { name: 'MINI Cooper Service', href: '/services/european-car-repair/mini-cooper' },
    { name: 'Clutch Repair', href: '/services/clutch-repair' },
    { name: 'Check Engine Light', href: '/services/diagnostic-check-engine-light' },
    { name: 'Wheel & Tire Service', href: '/services/wheel-and-tire' },
    { name: '--- DEALERSHIP ---', isHeader: true },
    { name: 'Financing', href: '/finance-application' },
    { name: 'Service', href: '/service' },
    { name: 'Trade-In', href: '/trade-in' },
    { name: 'AI Tools', href: '/ai-tools', icon: FiZap },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <>
      {/* Top Bar - Simplified */}
      <div className="bg-red-600 text-white py-2 hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <span>{settings?.phone || '(817) 382-2316'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                <span>{settings?.address ? settings.address.split(',')[0] : '305 Co Rd 3470'}, Paradise, TX</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{settings?.businessHours ? `Mon-Fri: ${settings.businessHours.monday?.open || '8AM'}-${settings.businessHours.friday?.close || '5PM'}` : 'Mon-Fri: 8AM-5PM'}</span>
              </div>
              <Link to="/vehicles" className="text-gray-300 hover:text-white transition-colors">
                Automobiles
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`fixed top-0 left-0 right-0 w-full transition-all duration-300 ${
          isScrolled ? 'backdrop-blur-xl shadow-lg bg-white/95' : 'bg-white backdrop-blur-sm'
        } ${isVisible ? 'translate-y-0' : 'lg:-translate-y-full'}`}
        style={{ zIndex: 9996 }}
      >
        {/* Desktop bar: full-width with better responsive layout */}
        <div className="hidden lg:flex items-center justify-between h-20 container mx-auto px-6 xl:px-6 2xl:px-8">
          {/* Left side: Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/assets/assets/images/logo.webp"
              alt="Paradise-Performance Logo"
              className="h-12 xl:h-14"
            />
          </Link>

          {/* Mega Menu Navigation */}
          <MegaMenu isScrolled={isScrolled} onBookService={() => setIsBookServiceOpen(true)} />
        </div>

        {/* Mobile bar: logo left, hamburger right */}
        <div className="flex lg:hidden justify-between items-center h-16 w-full px-4">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/assets/images/logo.webp"
              alt="Paradise-Performance Logo"
              className="h-10"
            />
          </Link>
          <button
            className={`p-3 rounded-lg transition-colors ${isMenuOpen ? 'bg-gray-100' : 'hover:bg-gray-100'} relative z-[60]`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Mobile Menu - Texas Tree Capital Style */}
        {/* Scrim/Backdrop */}
        <div 
          className={`fixed inset-0 bg-black/45 transition-opacity duration-200 lg:hidden ${
            isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          style={{ zIndex: 9998 }}
          onClick={() => setIsMenuOpen(false)}
        />
        
        {/* Mobile Drawer - Force visible for debugging */}
        {isMenuOpen && (
          <aside 
            className="fixed top-0 right-0 bg-white shadow-xl lg:hidden flex flex-col"
            style={{ 
              height: '100vh',
              width: '75%',
              maxWidth: '400px',
              zIndex: 9999
            }}
            aria-label="Mobile menu"
          >
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <img
                src="/assets/assets/images/logo.webp"
                alt="Paradise-Performance"
                className="h-8"
              />
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <SafeIcon icon={FiX} className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          
          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto py-3 px-2 bg-white">
            <nav className="space-y-1">
              {/* Home */}
              <Link 
                to="/" 
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              
              {/* Automobiles */}
              <Link 
                to="/vehicles" 
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Automobiles
              </Link>
              <Link 
                to="/inventory/bmw" 
                className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                onClick={() => setIsMenuOpen(false)}
              >
                — BMW Inventory
              </Link>
              <Link 
                to="/inventory/audi" 
                className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                onClick={() => setIsMenuOpen(false)}
              >
                — Audi Inventory
              </Link>
              <Link 
                to="/inventory/volkswagen" 
                className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                onClick={() => setIsMenuOpen(false)}
              >
                — VW Inventory
              </Link>
              
              {/* Golf Carts */}
              <Link 
                to="/golf-carts" 
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Golf Carts
              </Link>
              
              <Link 
                to="/golf-cart-services" 
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Golf Cart Services
              </Link>
              
              {/* Trailers */}
              <Link 
                to="/trailers" 
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Trailers
              </Link>
              
              {/* Services Accordion */}
              <div className="border-t border-gray-100 pt-1">
                <button
                  className="w-full px-4 py-3 text-left flex items-center justify-between text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileAccordions(prev => ({
                    services: !prev.services,
                    dealership: false
                  }))}
                  aria-expanded={mobileAccordions.services}
                >
                  <span>Repair Services</span>
                  <FiChevronDown 
                    className={`w-5 h-5 transition-transform duration-200 ${
                      mobileAccordions.services ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {mobileAccordions.services && (
                  <div className="px-3 pb-2 space-y-1">
                    <Link 
                      to="/services" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      All Services
                    </Link>
                    <Link 
                      to="/services/european-car-repair" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      European Vehicles
                    </Link>
                    <Link 
                      to="/services/european-car-repair/bmw" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      — BMW Service
                    </Link>
                    <Link 
                      to="/services/european-car-repair/mercedes" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      — Mercedes Service
                    </Link>
                    <Link 
                      to="/services/european-car-repair/audi" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      — Audi Service
                    </Link>
                    <Link 
                      to="/services/european-car-repair/porsche" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      — Porsche Service
                    </Link>
                    <Link 
                      to="/services/european-car-repair/volkswagen" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors pl-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      — Volkswagen Service
                    </Link>
                    <Link 
                      to="/services/diesel" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Diesel Repair
                    </Link>
                    <Link 
                      to="/services/clutch-repair" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Clutch Repair
                    </Link>
                    <Link 
                      to="/services/diagnostic-check-engine-light" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Check Engine Light
                    </Link>
                    <Link 
                      to="/services/wheel-and-tire" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Wheel & Tire Service
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Dealership Accordion */}
              <div className="border-t border-gray-100 pt-1">
                <button
                  className="w-full px-4 py-3 text-left flex items-center justify-between text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileAccordions(prev => ({
                    services: false,
                    dealership: !prev.dealership
                  }))}
                  aria-expanded={mobileAccordions.dealership}
                >
                  <span>Dealership</span>
                  <FiChevronDown 
                    className={`w-5 h-5 transition-transform duration-200 ${
                      mobileAccordions.dealership ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {mobileAccordions.dealership && (
                  <div className="px-3 pb-2 space-y-1">
                    <Link 
                      to="/finance-application" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Financing
                    </Link>
                    <Link 
                      to="/appointment" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Service Appointment
                    </Link>
                    <Link 
                      to="/trade-in" 
                      className="block px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Trade-In
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Other Links */}
              <div className="border-t border-gray-100 pt-1">
                <Link 
                  to="/ai-tools" 
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FiZap className="w-4 h-4" />
                  AI Tools
                </Link>
                <Link 
                  to="/about" 
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>
              </div>
              
            </nav>
          </div>
          
          {/* Sticky Bottom: CTAs + Contact Info */}
          <div className="border-t border-gray-200 bg-white px-4 py-4 space-y-2">
            <Link
              to="/vehicles"
              className="block bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-lg font-semibold text-center transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Browse Vehicles
            </Link>
            <button
              onClick={() => { setIsMenuOpen(false); setIsBookServiceOpen(true); }}
              className="block w-full border-2 border-red-600 text-red-600 hover:bg-red-50 px-5 py-3 rounded-lg font-semibold text-center transition-colors"
            >
              Schedule Service
            </button>
            <div className="text-sm text-gray-600 pt-2">
              <div><strong>Phone:</strong> {settings?.phone || '(817) 382-2316'}</div>
              <div><strong>Address:</strong> 305 Co Rd 3470, Paradise, TX</div>
            </div>
          </div>
        </aside>
        )}
      </header>
      <BookServiceModal isOpen={isBookServiceOpen} onClose={() => setIsBookServiceOpen(false)} />
    </>
  );
};

export default Header;